import { cn } from '@parcel/ui/lib/utils';
import type * as React from 'react';

interface IntegrationTileProps {
  size?: 'sm' | 'md';
  className?: string;
  children: React.ReactNode;
}

/** The square logo well shared by MCP client and connector cards. */
export function IntegrationTile({ size = 'sm', className, children }: IntegrationTileProps) {
  return (
    <span
      className={cn(
        'flex shrink-0 items-center justify-center overflow-hidden rounded-md border bg-muted/40',
        size === 'sm' ? 'size-9' : 'size-12',
        className,
      )}
    >
      {children}
    </span>
  );
}

interface IntegrationCardProps {
  logo: React.ReactNode;
  title: string;
  trailing?: React.ReactNode;
  children?: React.ReactNode;
  onActivate?: () => void;
  ariaLabel?: string;
  testId?: string;
  className?: string;
}

/** One card shape for both pages; passing onActivate makes the whole card a button. */
export function IntegrationCard({
  logo,
  title,
  trailing,
  children,
  onActivate,
  ariaLabel,
  testId,
  className,
}: IntegrationCardProps) {
  const body = (
    <>
      <div className="flex items-center gap-3">
        {logo}
        <h4 className="min-w-0 flex-1 truncate text-sm font-medium">{title}</h4>
        {trailing ? <div className="shrink-0">{trailing}</div> : null}
      </div>
      {children ? (
        <div className="mt-3 text-sm leading-5 text-muted-foreground">{children}</div>
      ) : null}
    </>
  );
  const base = 'flex h-full flex-col rounded-lg border p-4';

  if (!onActivate) {
    return (
      <article data-testid={testId} className={cn(base, className)}>
        {body}
      </article>
    );
  }
  return (
    <div
      data-testid={testId}
      // biome-ignore lint/a11y/useSemanticElements: block content (logo, heading, badge, text) cannot live inside a button element.
      role="button"
      tabIndex={0}
      aria-label={ariaLabel ?? title}
      className={cn(
        base,
        'cursor-pointer transition-colors hover:border-foreground/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        className,
      )}
      onClick={onActivate}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onActivate();
        }
      }}
    >
      {body}
    </div>
  );
}
